'use client'

import type { ActionDispatch } from 'react'
import { createContext, useContext, useEffect, useMemo, useState } from 'react'

import type {
  TopNavV5MobileAction,
  TopNavV5MobileState,
} from '@src/components/TopNavV5/TopNavV5Mobile/useTopNavV5MobileReducer'
import { useTopNavV5MobileReducer } from '@src/components/TopNavV5/TopNavV5Mobile/useTopNavV5MobileReducer'
import { useSession } from '@src/hooks/useSession'
import { useUnlocalizedPathname } from '@src/hooks/useUnlocalizedPathname'
import { useBreakpoints } from '@src/utils/breakpoints.util'

import type { TopNavV5MenuItem, TopNavV5Props } from './TopNavV5Props'

const TOP_NAV_DESKTOP_HEIGHT = 48

interface TopNavV5ContextType extends TopNavV5Props {
  expandedMenu: TopNavV5MenuItem | null
  setExpandedMenu: (menu: TopNavV5MenuItem | null) => void

  leftMenuIndex: number
  setLeftMenuIndex: (index: number) => void

  isExpanded: boolean
  setExpanded: (isExpanded: boolean) => void

  isAccountExpanded: boolean
  setAccountExpanded: (isExpanded: boolean) => void

  mobileState: TopNavV5MobileState
  mobileDispatch: ActionDispatch<[action: TopNavV5MobileAction]>

  topNavDesktopHeight: number
  topNavDesktopTopOffset: number
}

export const TopNavV5Context = createContext<TopNavV5ContextType>({
  expandedMenu: null,
  setExpandedMenu: () => {},

  leftMenuIndex: 0,
  setLeftMenuIndex: () => {},

  isExpanded: false,
  setExpanded: () => {},

  isAccountExpanded: false,
  setAccountExpanded: () => {},

  mobileState: {
    state: 'closed',
    previousState: null,
    menuItems: [],
    leftMenu: null,
    leftMenuIndex: null,
    subMenu: null,
    miniMenu: null,
  },
  mobileDispatch: () => {},

  topNavDesktopHeight: TOP_NAV_DESKTOP_HEIGHT,
  topNavDesktopTopOffset: TOP_NAV_DESKTOP_HEIGHT,
})

export const TopNavV5Provider = ({
  children,
  topNavProps,
}: {
  children: React.ReactNode
  topNavProps: TopNavV5Props
}) => {
  const { isComingFromLogin } = useSession()
  const pathname = useUnlocalizedPathname()
  const { isDesktop } = useBreakpoints()

  const [isExpanded, setExpanded] = useState(false)
  const [isAccountExpanded, setAccountExpanded] = useState(false)

  const [expandedMenu, setExpandedMenu] = useState<TopNavV5MenuItem | null>(null)
  const [leftMenuIndex, setLeftMenuIndex] = useState(0)

  const [mobileState, mobileDispatch] = useTopNavV5MobileReducer(topNavProps.items)

  const topNavDesktopHeight = TOP_NAV_DESKTOP_HEIGHT
  const [pencilBannerDesktopHeight, setPencilBannerDesktopHeight] = useState(0)

  useEffect(() => {
    if (!isDesktop) return
    const selector = '[data-builder-component="top-banner"]'

    // there can be multiple banners, find the one that's visible
    const visibleBanner = [...document.querySelectorAll(selector)].find((x) => {
      const rect = x.getBoundingClientRect()
      return (
        rect.top < window.innerHeight &&
        rect.bottom > 0 &&
        rect.left < window.innerWidth &&
        rect.right > 0
      )
    })

    if (visibleBanner) setPencilBannerDesktopHeight(visibleBanner.clientHeight)
  }, [isDesktop])

  useEffect(() => {
    const shouldExpandAccount =
      isDesktop &&
      isComingFromLogin &&
      !pathname.startsWith('/account') &&
      !pathname.endsWith('/affiliate-application')

    setAccountExpanded(shouldExpandAccount)
  }, [isComingFromLogin, pathname, isDesktop])

  const value = useMemo(
    () => ({
      ...topNavProps,
      isExpanded,
      setExpanded,

      leftMenuIndex,
      setLeftMenuIndex,

      expandedMenu,
      setExpandedMenu,

      isAccountExpanded,
      setAccountExpanded,

      mobileState,
      mobileDispatch,

      topNavDesktopHeight,
      topNavDesktopTopOffset: topNavDesktopHeight + pencilBannerDesktopHeight,
    }),
    [
      topNavProps,
      isExpanded,
      leftMenuIndex,
      expandedMenu,
      isAccountExpanded,
      mobileState,
      mobileDispatch,
      topNavDesktopHeight,
      pencilBannerDesktopHeight,
    ]
  )

  return <TopNavV5Context.Provider value={value}>{children}</TopNavV5Context.Provider>
}

export const useTopNavV5 = () => {
  const context = useContext(TopNavV5Context)
  if (!context) {
    throw new Error('useTopNavV5 must be used within a TopNavV5Provider')
  }
  return context
}
